/**
 * TMLoginPage — mobile-friendly login for /tm (Angels Army Team Manager)
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { useTMAuth } from '@/contexts/TMAuthContext';

const schema = z.object({
  email:    z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
type FormValues = z.infer<typeof schema>;

export default function TMLoginPage() {
  const navigate = useNavigate();
  const { firebaseUser, tmUser, loading } = useTMAuth();
  const [showPw, setShowPw] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  // Already logged in — redirect to appropriate page
  useEffect(() => {
    if (loading) return;
    if (!firebaseUser) return;
    if (!tmUser) { navigate('/tm/register', { replace: true }); return; }
    if (tmUser.status === 'pending') { navigate('/tm/pending', { replace: true }); return; }
    if (tmUser.status === 'approved') { navigate('/tm/dashboard', { replace: true }); return; }
  }, [loading, firebaseUser, tmUser, navigate]);

  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (v: FormValues) => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, v.email, v.password);
      navigate('/tm/dashboard');
    } catch {
      setError('Invalid email or password.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex flex-col items-center justify-center p-5">

      {/* Logo area */}
      <div className="text-center mb-8">
        <img src="/angels-army-logo.png" alt="Angels Army" className="w-20 h-20 object-contain mx-auto mb-3" />
        <h1 className="text-2xl font-bold text-gray-900">Angels Army</h1>
        <p className="text-sm text-gray-500 mt-1">Team Manager</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-gray-100 p-7">
        <h2 className="text-lg font-semibold text-gray-800 mb-5">Sign in to your account</h2>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                {...register('email')}
              />
            </div>
            {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 text-sm
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                {...register('password')}
              />
              <button type="button" onClick={() => setShowPw(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm
                       hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-60"
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-5">
          Don't have an account?{' '}
          <Link to="/tm/register" className="text-indigo-600 font-medium hover:underline">
            Request access
          </Link>
        </p>
      </div>

      <p className="text-xs text-gray-400 mt-6">Angels Army Ministry · WombCat</p>
    </div>
  );
}
